/**
 * AlgorithmRegistrationWizard — 5-step wizard for registering ML algorithms.
 * Phase 4.10 β7 — WQ-242.
 *
 * First wizard built on WQ-251 WizardModalShell from start (vs. retrofit).
 * Audit chain inherited via existing WQ-229 license_compliance instrumentation
 * in algorithm_registry_service.register_algorithm() — no new emission code.
 *
 * Steps:
 *   1. Identity                — name + category + description + slug (optional)
 *   2. Library & Capabilities  — library + library_version + interpretability +
 *                                compute_profile + problem_types + phase_available +
 *                                capability flags
 *   3. License & Origin        — license + origin_country, with live Gate 2 verdict
 *                                UI mirroring backend APPROVED/REJECTED sets and
 *                                AIPOA BLOCKED_COUNTRIES
 *   4. Schemas (Optional)      — data_requirements + hyperparameter_schema (JSON
 *                                textareas), evaluation_metrics chips, adapter_class
 *   5. Review & Register       — read-only summary; Register disabled if rejected
 *
 * Wraps existing POST /api/admin/ai/algorithms/. Backend remains source of truth
 * at submit (defense in depth).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  registerAlgorithm,
  classifyLicense,
  BLOCKED_ORIGIN_COUNTRIES,
  type AlgorithmRegisterRequest,
  type LicenseVerdict,
  type Algorithm,
} from '../../services/algorithmService';
import WizardModalShell from '../wizard/WizardModalShell';

type WizardStep = 1 | 2 | 3 | 4 | 5;

interface AlgorithmRegistrationWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted?: (algorithmId: string) => void;
}

const STEP_TITLES: Record<WizardStep, string> = {
  1: 'Identity',
  2: 'Library & capabilities',
  3: 'License & origin',
  4: 'Schemas (optional)',
  5: 'Review & register',
};

const ALL_CATEGORIES: string[] = [
  'classification', 'regression', 'gradient_boosting', 'ensemble',
  'clustering', 'anomaly_detection', 'dimensionality_reduction', 'time_series',
  'recommendation', 'deep_learning', 'nlp', 'survival', 'computer_vision',
];

const ALL_INTERPRETABILITY: string[] = ['high', 'medium', 'low', 'very_low'];

const ALL_COMPUTE_PROFILES: string[] = ['cpu', 'gpu_optional', 'gpu_required'];

const SLUG_RE = /^[a-z][a-z0-9-]*$/;

function isValidObjectJson(raw: string): boolean {
  try {
    const parsed = JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-primary)',
  color: 'var(--text-primary)', fontSize: 13,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6,
};

const AlgorithmRegistrationWizard: React.FC<AlgorithmRegistrationWizardProps> = ({
  isOpen, onClose, onSubmitted,
}) => {
  const { authFetch } = useAuth();
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1
  const [name, setName] = useState<string>('');
  const [category, setCategory] = useState<string>('classification');
  const [description, setDescription] = useState<string>('');
  const [slug, setSlug] = useState<string>('');

  // Step 2
  const [library, setLibrary] = useState<string>('');
  const [libraryVersion, setLibraryVersion] = useState<string>('');
  const [interpretability, setInterpretability] = useState<string>('medium');
  const [computeProfile, setComputeProfile] = useState<string>('cpu');
  const [phaseAvailable, setPhaseAvailable] = useState<string>('phase_5.0');
  const [problemTypesRaw, setProblemTypesRaw] = useState<string>('');
  const [supportsIncremental, setSupportsIncremental] = useState<boolean>(false);
  const [supportsFeatureImportance, setSupportsFeatureImportance] = useState<boolean>(false);
  const [supportsProbabilityOutput, setSupportsProbabilityOutput] = useState<boolean>(false);

  // Step 3
  const [license, setLicense] = useState<string>('');
  const [originCountry, setOriginCountry] = useState<string>('');

  // Step 4
  const [dataReqRaw, setDataReqRaw] = useState<string>('');
  const [hyperRaw, setHyperRaw] = useState<string>('');
  const [evaluationMetricsRaw, setEvaluationMetricsRaw] = useState<string>('');
  const [adapterClass, setAdapterClass] = useState<string>('');

  // Submit
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [launchError, setLaunchError] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setName('');
      setCategory('classification');
      setDescription('');
      setSlug('');
      setLibrary('');
      setLibraryVersion('');
      setInterpretability('medium');
      setComputeProfile('cpu');
      setPhaseAvailable('phase_5.0');
      setProblemTypesRaw('');
      setSupportsIncremental(false);
      setSupportsFeatureImportance(false);
      setSupportsProbabilityOutput(false);
      setLicense('');
      setOriginCountry('');
      setDataReqRaw('');
      setHyperRaw('');
      setEvaluationMetricsRaw('');
      setAdapterClass('');
      setSubmitting(false);
      setLaunchError(null);
    }
  }, [isOpen]);

  const problemTypes = useMemo(
    () => problemTypesRaw.split(',').map((s) => s.trim()).filter((s) => s.length > 0),
    [problemTypesRaw],
  );

  const evaluationMetrics = useMemo(
    () => evaluationMetricsRaw.split(',').map((s) => s.trim()).filter((s) => s.length > 0),
    [evaluationMetricsRaw],
  );

  const verdict: LicenseVerdict = useMemo(() => classifyLicense(license), [license]);

  const originBlocked = useMemo(() => {
    const code = originCountry.trim().toUpperCase();
    return code.length > 0 && BLOCKED_ORIGIN_COUNTRIES.has(code);
  }, [originCountry]);

  const slugValid = useMemo(() => {
    const s = slug.trim();
    return s.length === 0 || SLUG_RE.test(s);
  }, [slug]);

  const dataReqValid = useMemo(
    () => dataReqRaw.trim().length === 0 || isValidObjectJson(dataReqRaw),
    [dataReqRaw],
  );
  const hyperValid = useMemo(
    () => hyperRaw.trim().length === 0 || isValidObjectJson(hyperRaw),
    [hyperRaw],
  );

  const canAdvance = useMemo(() => {
    if (step === 1) {
      if (name.trim().length === 0) return false;
      if (!ALL_CATEGORIES.includes(category)) return false;
      if (!slugValid) return false;
      return true;
    }
    if (step === 2) {
      return library.trim().length > 0
        && interpretability.length > 0
        && computeProfile.length > 0
        && phaseAvailable.trim().length > 0
        && problemTypes.length >= 1;
    }
    if (step === 3) {
      if (license.trim().length === 0) return false;
      if (verdict === 'rejected') return false;
      if (originBlocked) return false;
      return true;
    }
    if (step === 4) {
      return dataReqValid && hyperValid;
    }
    return false;
  }, [
    step, name, category, slugValid, library, interpretability, computeProfile,
    phaseAvailable, problemTypes, license, verdict, originBlocked, dataReqValid, hyperValid,
  ]);

  const handleNext = () => {
    if (!canAdvance) return;
    setStep((prev) => (prev < 5 ? ((prev + 1) as WizardStep) : prev));
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? ((prev - 1) as WizardStep) : prev));
  };

  const handleRegister = async () => {
    setSubmitting(true);
    setLaunchError(null);
    try {
      const request: AlgorithmRegisterRequest = {
        name: name.trim(),
        category,
        description: description.trim() || undefined,
        slug: slug.trim() || undefined,
        library: library.trim(),
        library_version: libraryVersion.trim() || undefined,
        adapter_class: adapterClass.trim() || undefined,
        problem_types: problemTypes,
        interpretability_level: interpretability,
        compute_profile: computeProfile,
        phase_available: phaseAvailable.trim(),
        license: license.trim(),
        origin_country: originCountry.trim().toUpperCase() || undefined,
        data_requirements: dataReqRaw.trim() ? JSON.parse(dataReqRaw) : undefined,
        hyperparameter_schema: hyperRaw.trim() ? JSON.parse(hyperRaw) : undefined,
        evaluation_metrics: evaluationMetrics.length > 0 ? evaluationMetrics : undefined,
        supports_incremental: supportsIncremental,
        supports_feature_importance: supportsFeatureImportance,
        supports_probability_output: supportsProbabilityOutput,
        is_active: true,
      };
      const created: Algorithm = await registerAlgorithm(authFetch, request);
      if (onSubmitted) onSubmitted(created.id);
      onClose();
    } catch (err) {
      setLaunchError(err instanceof Error ? err.message : 'Failed to register algorithm');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WizardModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Register Algorithm"
      totalSteps={5}
      currentStep={step}
      stepTitle={STEP_TITLES[step]}
      testIdPrefix="alg-wizard"
      canAdvance={canAdvance}
      submitting={submitting}
      onBack={handleBack}
      onNext={handleNext}
      onSubmit={handleRegister}
      submitLabel="Register"
      submittingLabel="Registering..."
      launchError={launchError}
    >
      <>
        {step === 1 && (
          <div data-testid="alg-wizard-step-1">
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Algorithm name *</label>
              <input
                type="text"
                data-testid="alg-wizard-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="XGBoost Classifier"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Category *</label>
              <select
                data-testid="alg-wizard-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={inputStyle}
              >
                {ALL_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Description (optional)</label>
              <textarea
                data-testid="alg-wizard-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="What problem does this algorithm solve?"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Slug (optional — auto-derived if empty)</label>
              <input
                type="text"
                data-testid="alg-wizard-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="xgboost-classifier"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              {!slugValid && (
                <div data-testid="alg-wizard-slug-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  Slug must start with a lowercase letter and contain only lowercase letters, digits, and hyphens.
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="alg-wizard-step-2">
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Library *</label>
                <input
                  type="text"
                  data-testid="alg-wizard-library"
                  value={library}
                  onChange={(e) => setLibrary(e.target.value)}
                  placeholder="scikit-learn"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Version</label>
                <input
                  type="text"
                  data-testid="alg-wizard-library-version"
                  value={libraryVersion}
                  onChange={(e) => setLibraryVersion(e.target.value)}
                  placeholder="1.4.0"
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Interpretability *</label>
                <select
                  data-testid="alg-wizard-interpretability"
                  value={interpretability}
                  onChange={(e) => setInterpretability(e.target.value)}
                  style={inputStyle}
                >
                  {ALL_INTERPRETABILITY.map((i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Compute profile *</label>
                <select
                  data-testid="alg-wizard-compute-profile"
                  value={computeProfile}
                  onChange={(e) => setComputeProfile(e.target.value)}
                  style={inputStyle}
                >
                  {ALL_COMPUTE_PROFILES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Phase available *</label>
                <input
                  type="text"
                  data-testid="alg-wizard-phase-available"
                  value={phaseAvailable}
                  onChange={(e) => setPhaseAvailable(e.target.value)}
                  placeholder="phase_5.0"
                  style={inputStyle}
                />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Problem types * (comma-separated)</label>
              <input
                type="text"
                data-testid="alg-wizard-problem-types"
                value={problemTypesRaw}
                onChange={(e) => setProblemTypesRaw(e.target.value)}
                placeholder="binary_classification, multiclass_classification"
                style={inputStyle}
              />
              {problemTypes.length === 0 && problemTypesRaw.length > 0 && (
                <div data-testid="alg-wizard-problem-types-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  At least one problem type required.
                </div>
              )}
            </div>
            <div data-testid="alg-wizard-capabilities">
              <label style={labelStyle}>Capabilities</label>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    data-testid="alg-wizard-supports-incremental"
                    checked={supportsIncremental}
                    onChange={(e) => setSupportsIncremental(e.target.checked)}
                  />
                  Supports incremental
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    data-testid="alg-wizard-supports-feature-importance"
                    checked={supportsFeatureImportance}
                    onChange={(e) => setSupportsFeatureImportance(e.target.checked)}
                  />
                  Supports feature importance
                </label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="checkbox"
                    data-testid="alg-wizard-supports-probability-output"
                    checked={supportsProbabilityOutput}
                    onChange={(e) => setSupportsProbabilityOutput(e.target.checked)}
                  />
                  Supports probability output
                </label>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="alg-wizard-step-3">
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>License identifier *</label>
              <input
                type="text"
                data-testid="alg-wizard-license"
                value={license}
                onChange={(e) => setLicense(e.target.value)}
                placeholder="mit, apache-2.0, bsd-3-clause, ..."
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              <div data-testid="alg-wizard-license-verdict" style={{ marginTop: 8, fontSize: 12 }}>
                {license.trim().length === 0 && (
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Enter a license identifier to see Gate 2 verdict.
                  </span>
                )}
                {license.trim().length > 0 && verdict === 'approved' && (
                  <span style={{ color: '#22c55e' }}>✓ Approved (Gate 2)</span>
                )}
                {license.trim().length > 0 && verdict === 'flagged' && (
                  <span style={{ color: '#f59e0b' }}>
                    ⚠ Flagged — review required. Submit allowed; license escalates to Gate 2 review.
                  </span>
                )}
                {verdict === 'rejected' && (
                  <span style={{ color: '#dc2626' }}>
                    ✗ Rejected per Gate 2 allowlist. Cannot register.
                    <br />
                    Prohibited: GPL, AGPL, LGPL, MPL, EPL, CC-BY-SA, CDDL, OSL, CC-BY-NC.
                  </span>
                )}
              </div>
            </div>
            <div>
              <label style={labelStyle}>Origin country (optional, ISO-2)</label>
              <input
                type="text"
                data-testid="alg-wizard-origin-country"
                value={originCountry}
                onChange={(e) => setOriginCountry(e.target.value)}
                placeholder="US"
                maxLength={2}
                style={{ ...inputStyle, fontFamily: 'monospace', width: 80 }}
              />
              {originBlocked && (
                <div data-testid="alg-wizard-origin-blocked" style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>
                  ✗ Origin country '{originCountry.trim().toUpperCase()}' is blocked (AIPOA I-2).
                </div>
              )}
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="alg-wizard-step-4">
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Data requirements (JSON, optional)</label>
              <textarea
                data-testid="alg-wizard-data-requirements"
                value={dataReqRaw}
                onChange={(e) => setDataReqRaw(e.target.value)}
                rows={4}
                placeholder='{"min_rows": 100, "feature_types": ["numeric", "categorical"]}'
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              {!dataReqValid && (
                <div data-testid="alg-wizard-data-requirements-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  Must be a valid JSON object.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Hyperparameter schema (JSON, optional)</label>
              <textarea
                data-testid="alg-wizard-hyperparameter-schema"
                value={hyperRaw}
                onChange={(e) => setHyperRaw(e.target.value)}
                rows={4}
                placeholder='{"learning_rate": {"type": "float", "default": 0.1}}'
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
              {!hyperValid && (
                <div data-testid="alg-wizard-hyperparameter-schema-error" style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                  Must be a valid JSON object.
                </div>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Evaluation metrics (comma-separated, optional)</label>
              <input
                type="text"
                data-testid="alg-wizard-evaluation-metrics"
                value={evaluationMetricsRaw}
                onChange={(e) => setEvaluationMetricsRaw(e.target.value)}
                placeholder="accuracy, f1, roc_auc"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Adapter class (optional)</label>
              <input
                type="text"
                data-testid="alg-wizard-adapter-class"
                value={adapterClass}
                onChange={(e) => setAdapterClass(e.target.value)}
                placeholder="app.adapters.SklearnXGBoostAdapter"
                style={{ ...inputStyle, fontFamily: 'monospace' }}
              />
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="alg-wizard-step-5">
            <div style={{
              padding: 16, borderRadius: 8, border: '1px solid var(--border)',
              background: 'var(--bg-secondary)', fontSize: 13, lineHeight: 1.6,
            }}>
              <div data-testid="alg-wizard-review-summary">
                Register algorithm <strong>{name.trim()}</strong>
                {description.trim() ? ` — ${description.trim()}` : ''}.
                <br />
                Category: <strong>{category}</strong>
                {slug.trim() ? <> · Slug: <code>{slug.trim()}</code></> : null}
                <br />
                Library: <strong>{library.trim()}</strong>
                {libraryVersion.trim() ? ` (${libraryVersion.trim()})` : ''}
                <br />
                Interpretability: <strong>{interpretability}</strong> · Compute: <strong>{computeProfile}</strong> · Phase: <strong>{phaseAvailable.trim()}</strong>
                <br />
                Problem types: <strong>{problemTypes.join(', ') || '—'}</strong>
                <br />
                License: <code>{license.trim()}</code>{' '}
                <span data-testid="alg-wizard-review-verdict" style={{
                  color: verdict === 'approved' ? '#22c55e'
                    : verdict === 'flagged' ? '#f59e0b'
                    : '#dc2626',
                }}>
                  ({verdict})
                </span>
                {originCountry.trim() ? <> · Origin: <code>{originCountry.trim().toUpperCase()}</code></> : null}
                {evaluationMetrics.length > 0 ? <><br />Metrics: {evaluationMetrics.join(', ')}</> : null}
                {adapterClass.trim() ? <><br />Adapter: <code>{adapterClass.trim()}</code></> : null}
              </div>
              {verdict === 'rejected' && (
                <div data-testid="alg-wizard-review-rejected" style={{ marginTop: 12, color: '#dc2626' }}>
                  Cannot register: license is on the Gate 2 prohibited list.
                </div>
              )}
            </div>
          </div>
        )}
      </>
    </WizardModalShell>
  );
};

export default AlgorithmRegistrationWizard;
